import { AnimatePresence, Variants, motion } from 'framer-motion';
import { Fragment, useMemo } from 'react';
import { useLocation } from 'react-router';
import { Button, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { OutlineXIcon } from '@tempmee/react-icons';
import { Box, Center, Column, Portal, Row, ZStack } from '~/commonComponents';
import { IconButton } from '~/commonComponents/IconButton';
import { ModalOrder, useModal } from '~/components/ModalQueue';
import { useOfficeTutorial } from './Hooks';
import { TTutorialStepItem } from './Types';

const variants: Variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 100 : -100,
    opacity: 0,
    position: 'absolute' as const,
  }),
  center: {
    x: 0,
    opacity: 1,
    position: 'relative' as const,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 100 : -100,
    opacity: 0,
    position: 'absolute' as const,
  }),
};

export const OfficeTutorialPopup = () => {
  const {
    activeStep: [index, direction],
    tutorial,
    tutorialSteps,
    onNextSlide,
    onPrevSlide,
    onClose,
  } = useOfficeTutorial();

  const hasInitialImage = !!tutorialSteps?.initialImages;
  const isGettingStarted = index === -1 && hasInitialImage;
  const isLastSlide = tutorialSteps?.images && index === tutorialSteps?.images.length - 1;

  const currentItem: TTutorialStepItem | undefined = useMemo(() => {
    if (isGettingStarted) {
      return tutorialSteps.initialImages;
    }

    return tutorialSteps?.images[index];
  }, [index, isGettingStarted, tutorialSteps]);

  const totalSlides: number = useMemo(
    () => tutorialSteps?.images?.length || 0,
    [tutorialSteps?.images],
  );

  // Guard: only show the tutorial popup on allowed pages.
  // Without this, navigating away (e.g. to the T&C page) causes the modal queue
  // to auto-promote this popup once the higher-priority T&C popup is removed,
  // resulting in the tutorial appearing on unrelated pages (ARF-2149).
  const pathname = useLocation().pathname;
  const isOnAllowedPage = pathname === '/' || pathname === '/help';

  const { isActive } = useModal({
    id: ModalOrder.TUTORIAL_POPUP,
    condition: !!currentItem && !!tutorial && isOnAllowedPage,
  });

  if (!currentItem || !tutorial) {
    return null;
  }

  if (!isActive) {
    return null;
  }

  return (
    <Portal>
      <div className="modal" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
        <Box height={570} position="relative" data-cy={tutorial.id}>
          <ZStack top={-16} right={-16} zIndex={1000}>
            <IconButton
              Icon={<OutlineXIcon color={colors.shades[0]} />}
              buttonStyle={{
                backgroundColor: colors.secondary[500],
                borderRadius: '100%',
                height: 36,
                width: 36,
                boxShadow: 'rgba(0, 0, 0, 0.4) -1px 1px 8px 0px',
              }}
              onClick={onClose}
              dataCy="close-tutorial-popup-button"
            />
          </ZStack>
          <Column
            backgroundColor={colors.shades[0]}
            borderRadius={12}
            height={570}
            overflowY="auto"
            overflowX="hidden"
            width={560}
            position="relative"
          >
            <Center
              position="relative"
              width="100%"
              height={317}
              background="linear-gradient(180deg, #4FC0CF 0%, #4FCFC0 100%)"
            >
              <AnimatePresence custom={direction} mode="wait">
                <motion.img
                  key={currentItem.url}
                  src={currentItem.url}
                  alt={currentItem.title}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.4 }}
                  style={{
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'bottom',
                    width: 524,
                  }}
                />
              </AnimatePresence>
            </Center>
            <AnimatePresence custom={direction} mode="wait">
              <motion.div
                key={`${currentItem.title}-${currentItem.description}`}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4 }}
              >
                <Column gap={16} paddingInline={20}>
                  <Row justifyContent={isGettingStarted ? 'center' : 'start'} paddingTop={28}>
                    {!isGettingStarted && (
                      <Typography as="span" bold color={colors.primary[500]} textAlign="center">
                        {index + 1} of {totalSlides} -&nbsp;
                      </Typography>
                    )}
                    <Typography as="span" bold color={colors.primary[500]} textAlign="center">
                      {currentItem.title}
                    </Typography>
                  </Row>

                  <Typography
                    as="span"
                    color={colors.neutral[500]}
                    textAlign={isGettingStarted ? 'center' : 'left'}
                  >
                    {currentItem.description.split('\n').map((line, index) => (
                      <Fragment key={index}>
                        {line}
                        <br />
                      </Fragment>
                    ))}
                  </Typography>
                </Column>
              </motion.div>
            </AnimatePresence>
            <Row justifyContent="center" gap={16} marginTop="auto" paddingBottom={32}>
              {isGettingStarted ? (
                <Button size="md" onClick={onNextSlide} data-cy="tutorial-get-started-button">
                  Let&apos;s Get Started
                </Button>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    size="md"
                    onClick={onPrevSlide}
                    data-cy="tutorial-back-button"
                  >
                    Back
                  </Button>
                  <Button
                    size="md"
                    onClick={onNextSlide}
                    data-cy={isLastSlide ? 'tutorial-close-button' : 'tutorial-next-button'}
                  >
                    {isLastSlide ? 'Close' : 'Next'}
                  </Button>
                </>
              )}
            </Row>
          </Column>
        </Box>
      </div>
    </Portal>
  );
};
